<template>
  <div class="available-hours">
    <h2 class="section-title">
      <Badge :count="questionIndex" /> {{ $t('appointment_form_page.available_hours.title') }}
    </h2>
    <LoadingComponent v-show="loadingHours" :style="loadingMinHeight" />
    <ul ref="wrapper" class="hour-pills">
      <li
        v-show="!loadingHours"
        v-for="(hour, index) in availableHours"
        :key="index"
        class="hour-pill"
        :class="{ 'hour-pill--selected': selectedHour === hour, 'hour-pill--disabled': disabled }"
        @click="selectHour(hour)"
      >
        {{ hour }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, nextTick, useTemplateRef, onMounted } from 'vue'
import { Hour } from '~concierge/types/AppointmentTypes'
import LoadingComponent from '~conciergeUiCommons/components/LoadingComponent.vue'
import Badge from '~conciergeUiCommons/components/Badge.vue'

export default defineComponent({
  props: {
    availableHours: {
      type: Object as PropType<Record<number, Hour>>,
      required: true
    },
    selectedHour: {
      type: String as PropType<Hour | null>,
      required: false
    },
    loadingHours: {
      type: Boolean,
      default: true
    },
    questionIndex: {
      type: Number,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: { LoadingComponent, Badge },
  emits: ['select-hour'],
  setup(props, { expose }) {
    const wrapper = useTemplateRef('wrapper')
    const loaderHeight = ref<number>(100)
    onMounted(() => {
      nextTick(() => {
        if (wrapper.value) {
          scrollToViewport()
        }
      })
    })
    async function scrollToViewport() {
      if (wrapper.value && wrapper.value.clientHeight > loaderHeight.value) {
        loaderHeight.value = wrapper.value?.clientHeight
      }
      nextTick(() => {
        wrapper.value?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      })
    }
    expose({ scrollToViewport })
    return {
      loaderHeight,
      wrapper,
      scrollToViewport
    }
  },
  computed: {
    loadingMinHeight(): string {
      return `min-height: ${this.loaderHeight}px`
    }
  },
  methods: {
    selectHour(hour: Hour) {
      if (this.disabled) {
        return
      }
      this.$emit('select-hour', hour)
    }
  }
})
</script>

<style scoped lang="scss">
.section-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
  place-items: flex-end;
}

.hour-pills {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
  gap: 0.5rem;
  padding-inline: 0;
  padding-block: 1em;
  margin: 0 auto;
}

/* Limit to 2 or 3 columns based on available space */
@media (min-width: 600px) {
  .hours-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

li.hour-pill {
  background-color: var(--hour-picker-bg-color, transparent);
  color: var(--hour-picker-text-color, #212121);
  border: 1px solid var(--hour-picker-border-color, #ccc);
  border-radius: var(--vff-border-radius);
  cursor: pointer;
  transition:
    background-color 0.4s ease,
    border-color 0.3s ease 0s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: clamp(14px, 1rem, 1vw);
}

li.hour-pill:hover {
  background-color: var(--hour-picker-hover-bg-color, var(--dealer-primary-color));
  opacity: var(--hour-picker-hover-opacity, 0.9);
  color: var(--hour-picker-hover-text-color, var(--dealer-header-text));
  border-color: var(--hour-picker-hover-text-color, var(--dealer-header-text));
}

li.hour-pill--selected {
  background-color: var(--hour-picker-hover-bg-color, var(--dealer-primary-color));
  color: var(--hour-picker-hover-text-color, var(--dealer-header-text));
  border-color: var(--hour-picker-hover-text-color, var(--dealer-header-text));
}

li.hour-pill--selected:hover {
  background: var(--hour-picker-selected-hover-bg-color, var(--dealer-secondary-color));
  color: var(--hour-picker-selected-hover-text-color, var(--dealer-secondary-text-color));
  opacity: var(--hour-picker-selected-hover-opacity, 1);
}
</style>
